import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft, SubHeaderRight, SubheaderSeparator } from '../../../layout/SubHeader/SubHeader';
import Page from '../../../layout/Page/Page';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../../components/bootstrap/Card';
import Avatar from '../../../components/Avatar';
import Icon from '../../../components/icon/Icon';
import Badge from '../../../components/bootstrap/Badge';
import Button from '../../../components/bootstrap/Button';
import api from '../../../helpers/axios';
import Spinner from '../../../components/bootstrap/Spinner';
import { dashboardPagesMenu } from '../../../menu';
import Progress from '../../../components/bootstrap/Progress';
import Alert from '../../../components/bootstrap/Alert';
import Dropdown, { DropdownItem, DropdownMenu, DropdownToggle } from '../../../components/bootstrap/Dropdown';
import Modal, { ModalBody, ModalHeader, ModalFooter, ModalTitle } from '../../../components/bootstrap/Modal';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Textarea from '../../../components/bootstrap/forms/Textarea';
import showNotification from '../../../components/extras/showNotification';
import StateAssignmentModal from './StateAssignmentModal';

// User data interface matching AdminUserPayload
interface IUserDetails {
    userid: string;
    employeeId: string | null;
    userIdentificationId: number | null;
    email: string;
    username: string;
    firstname: string;
    surname: string;
    dateOfBirth: string | null;
    phoneNumber: string | null;
    alternatePhone: string | null;
    role: string;
    roles: string[] | null;
    department: string | null;
    designation: string | null;
    dateOfJoining: string | null;
    reportingManagerId: string | null;
    reportingManagerName: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    postalCode: string | null;
    profilePhotoUrl: string | null;
    identityProofType: string | null;
    identityProofNumber: string | null;
    identityProofUrl: string | null;
    accountStatus: string;
    isActive: boolean | null;
    emailVerified: boolean | null;
    languagePreference: string | null;
    timezone: string | null;
    createdAt: string | null;
    createdBy: string | null;
    updatedAt: string | null;
    updatedBy: string | null;
    lastLoginAt: string | null;
    lastActivityAt: string | null;
    failedLoginAttempts: number | null;
    passwordChangedAt: string | null;
    totalWebSessions: number | null;
    totalArticlesPublished: number | null;
    totalTimeSpentMinutes: number | null;
}

// Status badge configuration
const getStatusConfig = (status: string | undefined) => {
    const statusMap: Record<string, { color: 'success' | 'danger' | 'warning' | 'info' | 'secondary'; icon: string; label: string }> = {
        ACTIVE: { color: 'success', icon: 'CheckCircle', label: 'Active' },
        INACTIVE: { color: 'secondary', icon: 'DoNotDisturb', label: 'Inactive' },
        SUSPENDED: { color: 'danger', icon: 'Block', label: 'Suspended' },
        PENDING_APPROVAL: { color: 'warning', icon: 'HourglassEmpty', label: 'Pending Approval' },
        PENDING_VERIFICATION: { color: 'info', icon: 'MarkEmailUnread', label: 'Pending Verification' },
        LOCKED: { color: 'danger', icon: 'Lock', label: 'Locked' },
        TERMINATED: { color: 'danger', icon: 'PersonOff', label: 'Terminated' },
        ON_LEAVE: { color: 'info', icon: 'EventBusy', label: 'On Leave' },
    };
    return statusMap[status || 'ACTIVE'] || statusMap.ACTIVE;
};

// Role badge color
const getRoleColor = (role: string): 'primary' | 'success' | 'warning' | 'info' | 'secondary' => {
    const roleMap: Record<string, 'primary' | 'success' | 'warning' | 'info' | 'secondary'> = {
        ADMIN: 'primary',
        EDITOR: 'success',
        AUTHOR: 'info',
        PUBLISHER: 'warning',
        USER: 'secondary',
    };
    return roleMap[role?.toUpperCase()] || 'secondary';
};

const UserPage = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();

    // Role options for multi-select
    const ROLE_OPTIONS = [
        { value: 'ROLE_AUTHOR', label: 'Author' },
        { value: 'ROLE_EDITOR', label: 'Editor' },
        { value: 'ROLE_PUBLISHER', label: 'Publisher' },
        { value: 'ROLE_ADMIN', label: 'Administrator' },
    ];

    const [user, setUser] = useState<IUserDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    
    // Modal states
    const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState<boolean>(false);
    const [deactivateModalOpen, setDeactivateModalOpen] = useState<boolean>(false);
    const [activateModalOpen, setActivateModalOpen] = useState<boolean>(false);
    const [unlockModalOpen, setUnlockModalOpen] = useState<boolean>(false);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [stateAssignmentModalOpen, setStateAssignmentModalOpen] = useState<boolean>(false);
    
    // Form states
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [deactivateReason, setDeactivateReason] = useState<string>('');
    const [actionLoading, setActionLoading] = useState<boolean>(false);
    const [actionError, setActionError] = useState<string | null>(null);
    
    // Edit form states
    const [editFormData, setEditFormData] = useState<Partial<IUserDetails>>({});
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    
    // File upload states
    const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
    const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
    const [identityProofFile, setIdentityProofFile] = useState<File | null>(null);
    const [identityProofFileName, setIdentityProofFileName] = useState<string | null>(null);

    // Fetch user data
    const fetchUser = useCallback(async () => {
        if (!userId) return;
        
        setLoading(true);
        setError(null);
        try {
            const response = await api.get(`/api/admin/users/${userId}`);
            if (response.data.success) {
                setUser(response.data.user);
            } else {
                setError(response.data.message || 'Failed to fetch user details');
            }
        } catch (err: any) {
            console.error('Error fetching user:', err);
            setError(err.response?.data?.message || 'Failed to load user details. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    // Navigation handlers
    const handleBack = () => {
        navigate(`/${dashboardPagesMenu.manageUsers.path}`);
    };

    const handleOpenEditModal = () => {
        if (user) {
            // Parse roles from user data
            let userRoles: string[] = [];
            if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
                userRoles = user.roles.map(r => r.startsWith('ROLE_') ? r : `ROLE_${r}`);
            } else if (user.role) {
                // Parse comma-separated roles string
                userRoles = user.role.split(',').map(r => {
                    const trimmed = r.trim();
                    return trimmed.startsWith('ROLE_') ? trimmed : `ROLE_${trimmed}`;
                });
            }
            
            setSelectedRoles(userRoles);
            setEditFormData({
                // Personal Information
                firstname: user.firstname || '',
                surname: user.surname || '',
                email: user.email || '',
                phoneNumber: user.phoneNumber || '',
                alternatePhone: user.alternatePhone || '',
                dateOfBirth: user.dateOfBirth || '',
                
                // Employment Information
                employeeId: user.employeeId || '',
                dateOfJoining: user.dateOfJoining || '',
                role: user.role || '',
                department: user.department || '',
                designation: user.designation || '',
                reportingManagerId: user.reportingManagerId || '',
                
                // Address Information
                addressLine1: user.addressLine1 || '',
                addressLine2: user.addressLine2 || '',
                city: user.city || '',
                state: user.state || '',
                country: user.country || '',
                postalCode: user.postalCode || '',
                
                // Identity Verification
                identityProofType: user.identityProofType || '',
                identityProofNumber: user.identityProofNumber || '',
                
                // Preferences
                languagePreference: user.languagePreference || 'en',
                timezone: user.timezone || 'UTC',
            });
            setEditModalOpen(true);
            setActionError(null);
        }
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setEditFormData({});
        setActionError(null);
        setProfilePhotoFile(null);
        setProfilePhotoPreview(null);
        setIdentityProofFile(null);
        setIdentityProofFileName(null);
    };

    const handleEditFormChange = (field: keyof IUserDetails, value: string) => {
        setEditFormData(prev => ({ ...prev, [field]: value }));
    };

    // Handle role toggle for multi-select
    const handleRoleToggle = (role: string) => {
        setSelectedRoles(prev => {
            if (prev.includes(role)) {
                return prev.filter(r => r !== role);
            } else {
                return [...prev, role];
            }
        });
    };

    // Handle profile photo change
    const handleProfilePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setActionError('Please select a valid image file');
                return;
            }
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setActionError('Image size must be less than 5MB');
                return;
            }
            setProfilePhotoFile(file);
            setProfilePhotoPreview(URL.createObjectURL(file));
            setActionError(null);
        }
    };

    // Handle identity proof change
    const handleIdentityProofChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
            if (!validTypes.includes(file.type)) {
                setActionError('Please select a valid file (JPEG, PNG, GIF, or PDF)');
                return;
            }
            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                setActionError('File size must be less than 10MB');
                return;
            }
            setIdentityProofFile(file);
            setIdentityProofFileName(file.name);
            setActionError(null);
        }
    };

    // Upload profile photo
    const uploadProfilePhoto = async (userId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/api/admin/users/${userId}/profile-photo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    };

    // Upload identity proof
    const uploadIdentityProof = async (userId: string, file: File, proofType: string) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('proofType', proofType);
        await api.post(`/api/admin/users/${userId}/identity-proof`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    };

    // Delete profile photo
    const handleDeleteProfilePhoto = async () => {
        if (!userId) return;
        try {
            setActionLoading(true);
            await api.delete(`/api/admin/users/${userId}/profile-photo`);
            setProfilePhotoPreview(null);
            setProfilePhotoFile(null);
            if (user) {
                setUser({ ...user, profilePhotoUrl: null });
            }
            showNotification('Success', 'Profile photo removed successfully', 'success');
        } catch (err: any) {
            setActionError(err.response?.data?.message || 'Failed to remove profile photo');
        } finally {
            setActionLoading(false);
        }
    };

    // Delete identity proof
    const handleDeleteIdentityProof = async () => {
        if (!userId) return;
        try {
            setActionLoading(true);
            await api.delete(`/api/admin/users/${userId}/identity-proof`);
            setIdentityProofFile(null);
            setIdentityProofFileName(null);
            if (user) {
                setUser({ ...user, identityProofUrl: null });
            }
            showNotification('Success', 'Identity proof removed successfully', 'success');
        } catch (err: any) {
            setActionError(err.response?.data?.message || 'Failed to remove identity proof');
        } finally {
            setActionLoading(false);
        }
    };

    // Update User Handler
    const handleUpdateUser = async () => {
        if (!userId || !editFormData) return;
        
        // Validation
        if (!editFormData.firstname?.trim()) {
            setActionError('First name is required');
            return;
        }
        if (!editFormData.surname?.trim()) {
            setActionError('Surname is required');
            return;
        }
        if (!editFormData.email?.trim()) {
            setActionError('Email is required');
            return;
        }
        if (selectedRoles.length === 0) {
            setActionError('At least one role is required');
            return;
        }
        
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editFormData.email)) {
            setActionError('Please enter a valid email address');
            return;
        }

        setActionLoading(true);
        setActionError(null);
        
        try {
            // Include roles array in the payload
            const updatePayload = {
                ...editFormData,
                roles: selectedRoles,
            };
            console.log('Updating user with payload:', updatePayload);
            console.log('Selected roles:', selectedRoles);
            const response = await api.put(`/api/admin/users/${userId}`, updatePayload);
            
            if (response.data.success) {
                // Upload profile photo if selected
                if (profilePhotoFile) {
                    try {
                        await uploadProfilePhoto(userId, profilePhotoFile);
                    } catch (photoErr) {
                        console.error('Error uploading profile photo:', photoErr);
                    }
                }
                
                // Upload identity proof if selected
                if (identityProofFile && editFormData.identityProofType) {
                    try {
                        await uploadIdentityProof(userId, identityProofFile, editFormData.identityProofType);
                    } catch (proofErr) {
                        console.error('Error uploading identity proof:', proofErr);
                    }
                }
                
                showNotification(
                    'User Updated',
                    'User details have been updated successfully',
                    'success'
                );
                setEditModalOpen(false);
                setEditFormData({});
                setProfilePhotoFile(null);
                setProfilePhotoPreview(null);
                setIdentityProofFile(null);
                setIdentityProofFileName(null);
                await fetchUser();
            } else {
                setActionError(response.data.message || 'Failed to update user');
            }
        } catch (err: any) {
            console.error('Error updating user:', err);
            setActionError(err.response?.data?.message || 'Failed to update user. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    // Reset Password Handler
    const handleResetPassword = async () => {
        if (!userId || !newPassword) return;
        
        if (newPassword.length < 8) {
            setActionError('Password must be at least 8 characters');
            return;
        }
        
        if (newPassword !== confirmPassword) {
            setActionError('Passwords do not match');
            return;
        }

        // Password strength validation
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(newPassword)) {
            setActionError('Password must contain uppercase, lowercase, number, and special character');
            return;
        }

        setActionLoading(true);
        setActionError(null);
        
        try {
            const response = await api.post(`/api/admin/users/${userId}/reset-password`, {
                newPassword: newPassword
            });
            
            if (response.data.success) {
                showNotification(
                    'Password Reset',
                    'Password has been reset successfully',
                    'success'
                );
                setResetPasswordModalOpen(false);
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setActionError(response.data.message || 'Failed to reset password');
            }
        } catch (err: any) {
            console.error('Error resetting password:', err);
            setActionError(err.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    // Deactivate User Handler
    const handleDeactivateUser = async () => {
        if (!userId) return;
        
        if (!deactivateReason.trim()) {
            setActionError('Please provide a reason for deactivation');
            return;
        }

        setActionLoading(true);
        setActionError(null);
        
        try {
            const response = await api.post(`/api/admin/users/${userId}/deactivate`, {
                reason: deactivateReason
            });
            
            if (response.data.success) {
                showNotification(
                    'User Deactivated',
                    `${getFullName()} has been deactivated successfully`,
                    'info'
                );
                setDeactivateModalOpen(false);
                setDeactivateReason('');
                // Refresh user data to show updated status
                await fetchUser();
            } else {
                setActionError(response.data.message || 'Failed to deactivate user');
            }
        } catch (err: any) {
            console.error('Error deactivating user:', err);
            setActionError(err.response?.data?.message || 'Failed to deactivate user. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    // Activate User Handler
    const handleActivateUser = async () => {
        if (!userId) return;

        setActionLoading(true);
        setActionError(null);
        
        try {
            const response = await api.post(`/api/admin/users/${userId}/activate`);
            
            if (response.data.success) {
                showNotification(
                    'User Activated',
                    `${getFullName()} has been activated successfully`,
                    'success'
                );
                setActivateModalOpen(false);
                await fetchUser();
            } else {
                setActionError(response.data.message || 'Failed to activate user');
            }
        } catch (err: any) {
            console.error('Error activating user:', err);
            setActionError(err.response?.data?.message || 'Failed to activate user. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    // Unlock Account Handler
    const handleUnlockAccount = async () => {
        if (!userId) return;

        setActionLoading(true);
        setActionError(null);
        
        try {
            const response = await api.post(`/api/admin/users/${userId}/unlock`);
            
            if (response.data.success) {
                showNotification(
                    'Account Unlocked',
                    `${getFullName()}'s account has been unlocked successfully`,
                    'success'
                );
                setUnlockModalOpen(false);
                await fetchUser();
            } else {
                setActionError(response.data.message || 'Failed to unlock account');
            }
        } catch (err: any) {
            console.error('Error unlocking account:', err);
            setActionError(err.response?.data?.message || 'Failed to unlock account. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    // Force Password Change Handler
    const handleForcePasswordChange = async () => {
        if (!userId) return;

        setActionLoading(true);
        
        try {
            const response = await api.post(`/api/admin/users/${userId}/force-password-change`);
            
            if (response.data.success) {
                showNotification(
                    'Password Change Required',
                    'User will be prompted to change password on next login',
                    'info'
                );
            }
        } catch (err: any) {
            console.error('Error forcing password change:', err);
            showNotification(
                'Error',
                err.response?.data?.message || 'Failed to set password change requirement',
                'danger'
            );
        } finally {
            setActionLoading(false);
        }
    };

    // Clear modal states when closing
    const handleCloseResetPasswordModal = () => {
        setResetPasswordModalOpen(false);
        setNewPassword('');
        setConfirmPassword('');
        setActionError(null);
    };

    const handleCloseDeactivateModal = () => {
        setDeactivateModalOpen(false);
        setDeactivateReason('');
        setActionError(null);
    };

    const formatDate = (date: string | null, format: string = 'MMM D, YYYY') => {
        if (!date) return '-';
        return dayjs(date).format(format);
    };

    const formatDateTime = (date: string | null) => {
        if (!date) return '-';
        return dayjs(date).format('MMM D, YYYY h:mm A');
    };

    const getFullAddress = () => {
        if (!user) return '-';
        const parts = [
            user.addressLine1,
            user.addressLine2,
            user.city,
            user.state,
            user.postalCode,
            user.country
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : '-';
    };

    const getFullName = () => {
        if (!user) return '';
        return `${user.firstname || ''} ${user.surname || ''}`.trim() || user.username;
    };

    const isContentRole = () => {
        if (!user) return false;
        const roleStr = (user.roles?.join(',') || user.role || '').toUpperCase();
        return roleStr.includes('EDITOR') || roleStr.includes('PUBLISHER') || roleStr.includes('AUTHOR');
    };

    const calculateProfileCompletion = () => {
        if (!user) return 0;
        const fields = [
            user.firstname, user.surname, user.email, user.phoneNumber,
            user.department, user.designation, user.dateOfJoining,
            user.addressLine1, user.city, user.state, user.country,
            user.profilePhotoUrl, user.identityProofType, user.identityProofNumber
        ];
        const filled = fields.filter(Boolean).length;
        return Math.round((filled / fields.length) * 100);
    };

    if (loading) {
        return (
            <PageWrapper title="User Details">
                <Page>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="text-center">
                            <Spinner size={48} color="primary" />
                            <p className="mt-3 text-muted">Loading user details...</p>
                        </div>
                    </div>
                </Page>
            </PageWrapper>
        );
    }

    if (error || !user) {
        return (
            <PageWrapper title="User Details">
                <SubHeader>
                    <SubHeaderLeft>
                        <Button color="link" onClick={handleBack}>
                            <Icon icon="ArrowBack" className="me-1" /> Back to Users
                        </Button>
                    </SubHeaderLeft>
                </SubHeader>
                <Page>
                    <Alert color="danger" icon="Error" className="d-flex align-items-center">
                        <div>
                            <h5 className="alert-heading">User Not Found</h5>
                            <p className="mb-0">{error || 'The requested user could not be found.'}</p>
                        </div>
                    </Alert>
                </Page>
            </PageWrapper>
        );
    }

    const statusConfig = getStatusConfig(user.accountStatus);
    const profileCompletion = calculateProfileCompletion();

    return (
        <>
        <PageWrapper title={`User: ${getFullName()}`}>
            <SubHeader>
                <SubHeaderLeft>
                    <Button color="link" onClick={handleBack}>
                        <Icon icon="ArrowBack" className="me-1" /> Back to Users
                    </Button>
                    <SubheaderSeparator />
                    <span className="fw-bold fs-5">{getFullName()}</span>
                    <Badge color={statusConfig.color} className="ms-2">
                        <Icon icon={statusConfig.icon} className="me-1" size="sm" />
                        {statusConfig.label}
                    </Badge>
                </SubHeaderLeft>
                <SubHeaderRight>
                    <Dropdown>
                        <DropdownToggle hasIcon={false}>
                            <Button color="light" icon="MoreVert" />
                        </DropdownToggle>
                        <DropdownMenu isAlignmentEnd>
                            <DropdownItem>
                                <Button 
                                    color="link" 
                                    className="dropdown-item w-100 text-start"
                                    onClick={() => setResetPasswordModalOpen(true)}
                                >
                                    <Icon icon="Lock" className="me-2" /> Reset Password
                                </Button>
                            </DropdownItem>
                            <DropdownItem>
                                <Button 
                                    color="link" 
                                    className="dropdown-item w-100 text-start"
                                    onClick={handleForcePasswordChange}
                                    isDisable={actionLoading}
                                >
                                    <Icon icon="VpnKey" className="me-2" /> Force Password Change
                                </Button>
                            </DropdownItem>
                            <DropdownItem isDivider />
                            {user.accountStatus === 'LOCKED' && (
                                <DropdownItem>
                                    <Button 
                                        color="link" 
                                        className="dropdown-item w-100 text-start text-success"
                                        onClick={() => setUnlockModalOpen(true)}
                                    >
                                        <Icon icon="LockOpen" className="me-2" /> Unlock Account
                                    </Button>
                                </DropdownItem>
                            )}
                            {user.accountStatus === 'ACTIVE' ? (
                                <DropdownItem>
                                    <Button 
                                        color="link" 
                                        className="dropdown-item w-100 text-start text-danger"
                                        onClick={() => setDeactivateModalOpen(true)}
                                    >
                                        <Icon icon="PersonOff" className="me-2" /> Deactivate User
                                    </Button>
                                </DropdownItem>
                            ) : (
                                <DropdownItem>
                                    <Button 
                                        color="link" 
                                        className="dropdown-item w-100 text-start text-success"
                                        onClick={() => setActivateModalOpen(true)}
                                    >
                                        <Icon icon="PersonAdd" className="me-2" /> Activate User
                                    </Button>
                                </DropdownItem>
                            )}
                        </DropdownMenu>
                    </Dropdown>
                    {isContentRole() && (
                        <Button
                            color="info"
                            icon="Map"
                            isOutline
                            onClick={() => setStateAssignmentModalOpen(true)}
                            className="me-2">
                            State Assignments
                        </Button>
                    )}
                    <Button color="primary" icon="Edit" onClick={handleOpenEditModal}>
                        Edit User
                    </Button>
                </SubHeaderRight>
            </SubHeader>

            <Page>
                <div className="row g-4">
                    {/* Profile Card - Left Column */}
                    <div className="col-lg-4 col-md-5">
                        {/* Profile Overview Card */}
                        <Card className="shadow-sm">
                            <CardBody className="text-center py-4">
                                <div className="position-relative d-inline-block mb-3">
                                    <Avatar
                                        size={120}
                                        src={user.profilePhotoUrl || ''}
                                        color={getRoleColor(user.role)}
                                        userName={getFullName()}
                                        className="border border-4 border-light shadow"
                                    />
                                    <span 
                                        className={`position-absolute bottom-0 end-0 rounded-circle p-2 border border-3 border-light ${
                                            user.accountStatus === 'ACTIVE' ? 'bg-success' : 'bg-secondary'
                                        }`}
                                        style={{ width: '24px', height: '24px' }}
                                    />
                                </div>
                                <h4 className="fw-bold mb-1">{getFullName()}</h4>
                                <p className="text-muted mb-2">@{user.username}</p>
                                <Badge color={getRoleColor(user.role)} className="px-3 py-2 fs-6">
                                    {user.role}
                                </Badge>
                                {user.designation && (
                                    <p className="text-muted mt-2 mb-0">
                                        <Icon icon="Work" className="me-1" />
                                        {user.designation}
                                    </p>
                                )}
                                {user.department && (
                                    <p className="text-muted mb-0">
                                        <Icon icon="Business" className="me-1" />
                                        {user.department}
                                    </p>
                                )}
                            </CardBody>
                        </Card>

                        {/* Profile Completion Card */}
                        <Card className="shadow-sm mt-4">
                            <CardHeader>
                                <CardLabel icon="Assessment" iconColor="info">
                                    <CardTitle>Profile Completion</CardTitle>
                                </CardLabel>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="fw-semibold">Completion</span>
                                    <span className="fw-bold text-primary">{profileCompletion}%</span>
                                </div>
                                <Progress 
                                    value={profileCompletion} 
                                    color={profileCompletion >= 80 ? 'success' : profileCompletion >= 50 ? 'warning' : 'danger'}
                                    height={10}
                                    isAnimated
                                />
                                {profileCompletion < 100 && (
                                    <p className="text-muted small mt-2 mb-0">
                                        Complete the profile to unlock all features
                                    </p>
                                )}
                            </CardBody>
                        </Card>

                        {/* Quick Stats Card */}
                        <Card className="shadow-sm mt-4">
                            <CardHeader>
                                <CardLabel icon="Analytics" iconColor="success">
                                    <CardTitle>Activity Stats</CardTitle>
                                </CardLabel>
                            </CardHeader>
                            <CardBody>
                                <div className="row g-3">
                                    <div className="col-4 text-center">
                                        <div className="bg-light rounded p-3">
                                            <Icon icon="Login" size="2x" color="primary" />
                                            <h5 className="fw-bold mt-2 mb-0">{user.totalWebSessions || 0}</h5>
                                            <small className="text-muted">Sessions</small>
                                        </div>
                                    </div>
                                    <div className="col-4 text-center">
                                        <div className="bg-light rounded p-3">
                                            <Icon icon="Article" size="2x" color="success" />
                                            <h5 className="fw-bold mt-2 mb-0">{user.totalArticlesPublished || 0}</h5>
                                            <small className="text-muted">Articles</small>
                                        </div>
                                    </div>
                                    <div className="col-4 text-center">
                                        <div className="bg-light rounded p-3">
                                            <Icon icon="Schedule" size="2x" color="info" />
                                            <h5 className="fw-bold mt-2 mb-0">{user.totalTimeSpentMinutes || 0}</h5>
                                            <small className="text-muted">Minutes</small>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* State Assignments Card — only for content-producing roles */}
                        {isContentRole() && (
                            <Card className="shadow-sm mt-4">
                                <CardHeader>
                                    <CardLabel icon="Map" iconColor="info">
                                        <CardTitle>State Assignments</CardTitle>
                                    </CardLabel>
                                </CardHeader>
                                <CardBody>
                                    <p className="text-muted small mb-3">
                                        Controls which states this user can create/review articles for.
                                        Empty = unrestricted access to all states.
                                    </p>
                                    <Button
                                        color="info"
                                        isOutline
                                        icon="Map"
                                        className="w-100"
                                        onClick={() => setStateAssignmentModalOpen(true)}>
                                        Manage State Assignments
                                    </Button>
                                </CardBody>
                            </Card>
                        )}
                    </div>

                    {/* Details Cards - Right Column */}
                    <div className="col-lg-8 col-md-7">
                        {/* Personal Information */}
                        <Card className="shadow-sm">
                            <CardHeader>
                                <CardLabel icon="Person" iconColor="primary">
                                    <CardTitle>Personal Information</CardTitle>
                                </CardLabel>
                            </CardHeader>
                            <CardBody>
                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-primary-subtle rounded p-2">
                                                    <Icon icon="Person" color="primary" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Full Name</small>
                                                <strong>{getFullName()}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-info-subtle rounded p-2">
                                                    <Icon icon="Email" color="info" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Email Address</small>
                                                <strong>{user.email}</strong>
                                                {user.emailVerified && (
                                                    <Icon icon="Verified" color="success" className="ms-1" />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-success-subtle rounded p-2">
                                                    <Icon icon="Phone" color="success" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Phone Number</small>
                                                <strong>{user.phoneNumber || '-'}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-warning-subtle rounded p-2">
                                                    <Icon icon="Cake" color="warning" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Date of Birth</small>
                                                <strong>{formatDate(user.dateOfBirth)}</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Employment Information */}
                        <Card className="shadow-sm mt-4">
                            <CardHeader>
                                <CardLabel icon="Work" iconColor="success">
                                    <CardTitle>Employment Details</CardTitle>
                                </CardLabel>
                            </CardHeader>
                            <CardBody>
                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-primary-subtle rounded p-2">
                                                    <Icon icon="Badge" color="primary" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Employee ID</small>
                                                <strong>{user.employeeId || '-'}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-success-subtle rounded p-2">
                                                    <Icon icon="Business" color="success" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Department</small>
                                                <strong>{user.department || '-'}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-info-subtle rounded p-2">
                                                    <Icon icon="WorkOutline" color="info" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Designation</small>
                                                <strong>{user.designation || '-'}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-warning-subtle rounded p-2">
                                                    <Icon icon="CalendarToday" color="warning" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Date of Joining</small>
                                                <strong>{formatDate(user.dateOfJoining)}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-secondary-subtle rounded p-2">
                                                    <Icon icon="SupervisorAccount" color="secondary" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Reporting Manager</small>
                                                <strong>{user.reportingManagerName || '-'}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-danger-subtle rounded p-2">
                                                    <Icon icon="AdminPanelSettings" color="danger" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Role</small>
                                                <Badge color={getRoleColor(user.role)} className="mt-1">
                                                    {user.role}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Address Information */}
                        <Card className="shadow-sm mt-4">
                            <CardHeader>
                                <CardLabel icon="LocationOn" iconColor="danger">
                                    <CardTitle>Address Information</CardTitle>
                                </CardLabel>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex align-items-start">
                                    <div className="flex-shrink-0">
                                        <div className="bg-danger-subtle rounded p-2">
                                            <Icon icon="Home" color="danger" size="lg" />
                                        </div>
                                    </div>
                                    <div className="flex-grow-1 ms-3">
                                        <small className="text-muted d-block">Full Address</small>
                                        <strong>{getFullAddress()}</strong>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Identity Verification */}
                        <Card className="shadow-sm mt-4">
                            <CardHeader>
                                <CardLabel icon="VerifiedUser" iconColor="info">
                                    <CardTitle>Identity Verification</CardTitle>
                                </CardLabel>
                            </CardHeader>
                            <CardBody>
                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-info-subtle rounded p-2">
                                                    <Icon icon="CreditCard" color="info" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">ID Type</small>
                                                <strong>{user.identityProofType || '-'}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-secondary-subtle rounded p-2">
                                                    <Icon icon="Numbers" color="secondary" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">ID Number</small>
                                                <strong>
                                                    {user.identityProofNumber 
                                                        ? `****${user.identityProofNumber.slice(-4)}` 
                                                        : '-'}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Account & Security */}
                        <Card className="shadow-sm mt-4">
                            <CardHeader>
                                <CardLabel icon="Security" iconColor="warning">
                                    <CardTitle>Account & Security</CardTitle>
                                </CardLabel>
                            </CardHeader>
                            <CardBody>
                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-success-subtle rounded p-2">
                                                    <Icon icon="VerifiedUser" color="success" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Account Status</small>
                                                <Badge color={statusConfig.color}>
                                                    <Icon icon={statusConfig.icon} className="me-1" size="sm" />
                                                    {statusConfig.label}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-primary-subtle rounded p-2">
                                                    <Icon icon="Login" color="primary" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Last Login</small>
                                                <strong>{formatDateTime(user.lastLoginAt)}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-info-subtle rounded p-2">
                                                    <Icon icon="Event" color="info" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Account Created</small>
                                                <strong>{formatDateTime(user.createdAt)}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-warning-subtle rounded p-2">
                                                    <Icon icon="VpnKey" color="warning" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Password Changed</small>
                                                <strong>{formatDateTime(user.passwordChangedAt)}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className={`rounded p-2 ${user.failedLoginAttempts && user.failedLoginAttempts > 3 ? 'bg-danger-subtle' : 'bg-secondary-subtle'}`}>
                                                    <Icon 
                                                        icon="Warning" 
                                                        color={user.failedLoginAttempts && user.failedLoginAttempts > 3 ? 'danger' : 'secondary'} 
                                                        size="lg" 
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Failed Login Attempts</small>
                                                <strong className={user.failedLoginAttempts && user.failedLoginAttempts > 3 ? 'text-danger' : ''}>
                                                    {user.failedLoginAttempts || 0}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div className="bg-secondary-subtle rounded p-2">
                                                    <Icon icon="PersonAdd" color="secondary" size="lg" />
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <small className="text-muted d-block">Created By</small>
                                                <strong>{user.createdBy || 'System'}</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Page>
        </PageWrapper>

        {/* Reset Password Modal */}
            <Modal
                isOpen={resetPasswordModalOpen}
                setIsOpen={setResetPasswordModalOpen}
                size="lg"
                titleId="reset-password-modal"
                isCentered
                isStaticBackdrop
            >
                <ModalHeader setIsOpen={setResetPasswordModalOpen}>
                    <ModalTitle id="reset-password-modal">
                        <Icon icon="VpnKey" className="me-2" />
                        Reset Password
                    </ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <p className="text-muted mb-4">
                            Set a new password for <strong>{user.username}</strong>. 
                            The user will need to use this password on their next login.
                        </p>
                        
                        {actionError && (
                            <div className="alert alert-danger mb-3" role="alert">
                                <Icon icon="Error" className="me-2" />
                                {actionError}
                            </div>
                        )}

                        <FormGroup id="newPassword" label="New Password" isFloating>
                            <Input
                                type="password"
                                placeholder="New Password"
                                value={newPassword}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                            />
                        </FormGroup>
                        
                        <FormGroup id="confirmPassword" label="Confirm Password" isFloating className="mt-3">
                            <Input
                                type="password"
                                placeholder="Confirm Password"
                                value={confirmPassword}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                            />
                        </FormGroup>

                        {newPassword && (
                            <div className="mt-3">
                                <small className="text-muted">Password Requirements:</small>
                                <ul className="small mb-0 mt-1">
                                    <li className={newPassword.length >= 8 ? 'text-success' : 'text-danger'}>
                                        <Icon icon={newPassword.length >= 8 ? 'Check' : 'Close'} size="sm" className="me-1" />
                                        At least 8 characters
                                    </li>
                                    <li className={/[A-Z]/.test(newPassword) ? 'text-success' : 'text-danger'}>
                                        <Icon icon={/[A-Z]/.test(newPassword) ? 'Check' : 'Close'} size="sm" className="me-1" />
                                        One uppercase letter
                                    </li>
                                    <li className={/[a-z]/.test(newPassword) ? 'text-success' : 'text-danger'}>
                                        <Icon icon={/[a-z]/.test(newPassword) ? 'Check' : 'Close'} size="sm" className="me-1" />
                                        One lowercase letter
                                    </li>
                                    <li className={/[0-9]/.test(newPassword) ? 'text-success' : 'text-danger'}>
                                        <Icon icon={/[0-9]/.test(newPassword) ? 'Check' : 'Close'} size="sm" className="me-1" />
                                        One number
                                    </li>
                                    <li className={newPassword === confirmPassword && confirmPassword.length > 0 ? 'text-success' : 'text-danger'}>
                                        <Icon icon={newPassword === confirmPassword && confirmPassword.length > 0 ? 'Check' : 'Close'} size="sm" className="me-1" />
                                        Passwords match
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        isOutline
                        onClick={handleCloseResetPasswordModal}
                        isDisable={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        icon="VpnKey"
                        onClick={handleResetPassword}
                        isDisable={actionLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword || newPassword.length < 8}
                    >
                        {actionLoading ? (
                            <>
                                <Spinner size={16} className="me-2" />
                                Resetting...
                            </>
                        ) : (
                            'Reset Password'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Deactivate User Modal */}
            <Modal
                isOpen={deactivateModalOpen}
                setIsOpen={setDeactivateModalOpen}
                size="lg"
                titleId="deactivate-user-modal"
                isCentered
                isStaticBackdrop
            >
                <ModalHeader setIsOpen={setDeactivateModalOpen}>
                    <ModalTitle id="deactivate-user-modal">
                        <Icon icon="PersonOff" className="me-2 text-danger" />
                        Deactivate User Account
                    </ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <div className="alert alert-warning d-flex align-items-center" role="alert">
                            <Icon icon="Warning" size="lg" className="me-3" />
                            <div>
                                <strong>Warning:</strong> This action will prevent the user from accessing the system.
                            </div>
                        </div>
                        
                        <p className="text-muted mb-4">
                            You are about to deactivate the account for <strong>{user.username}</strong>. 
                            The user will not be able to log in until their account is reactivated.
                        </p>

                        {actionError && (
                            <div className="alert alert-danger mb-3" role="alert">
                                <Icon icon="Error" className="me-2" />
                                {actionError}
                            </div>
                        )}

                        <FormGroup id="deactivateReason" label="Reason for Deactivation" isFloating>
                            <Textarea
                                placeholder="Reason for Deactivation"
                                value={deactivateReason}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDeactivateReason(e.target.value)}
                                rows={3}
                            />
                        </FormGroup>
                        <small className="text-muted">
                            Providing a reason helps maintain an audit trail for compliance purposes.
                        </small>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        isOutline
                        onClick={handleCloseDeactivateModal}
                        isDisable={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="danger"
                        icon="PersonOff"
                        onClick={handleDeactivateUser}
                        isDisable={actionLoading || !deactivateReason.trim()}
                    >
                        {actionLoading ? (
                            <>
                                <Spinner size={16} className="me-2" />
                                Deactivating...
                            </>
                        ) : (
                            'Deactivate Account'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Activate User Modal */}
            <Modal
                isOpen={activateModalOpen}
                setIsOpen={setActivateModalOpen}
                size="lg"
                titleId="activate-user-modal"
                isCentered
                isStaticBackdrop
            >
                <ModalHeader setIsOpen={setActivateModalOpen}>
                    <ModalTitle id="activate-user-modal">
                        <Icon icon="PersonAddAlt1" className="me-2 text-success" />
                        Activate User Account
                    </ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <div className="alert alert-info d-flex align-items-center" role="alert">
                            <Icon icon="Info" size="lg" className="me-3" />
                            <div>
                                This will restore user access to the system.
                            </div>
                        </div>
                        
                        <p className="text-muted mb-0">
                            You are about to activate the account for <strong>{user.username}</strong>. 
                            After activation, the user will be able to log in with their existing credentials.
                        </p>

                        {actionError && (
                            <div className="alert alert-danger mt-3" role="alert">
                                <Icon icon="Error" className="me-2" />
                                {actionError}
                            </div>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        isOutline
                        onClick={() => setActivateModalOpen(false)}
                        isDisable={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="success"
                        icon="PersonAddAlt1"
                        onClick={handleActivateUser}
                        isDisable={actionLoading}
                    >
                        {actionLoading ? (
                            <>
                                <Spinner size={16} className="me-2" />
                                Activating...
                            </>
                        ) : (
                            'Activate Account'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Unlock Account Modal */}
            <Modal
                isOpen={unlockModalOpen}
                setIsOpen={setUnlockModalOpen}
                size="lg"
                titleId="unlock-account-modal"
                isCentered
                isStaticBackdrop
            >
                <ModalHeader setIsOpen={setUnlockModalOpen}>
                    <ModalTitle id="unlock-account-modal">
                        <Icon icon="LockOpen" className="me-2 text-warning" />
                        Unlock User Account
                    </ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <div className="alert alert-warning d-flex align-items-center" role="alert">
                            <Icon icon="Lock" size="lg" className="me-3" />
                            <div>
                                This account was locked due to multiple failed login attempts.
                            </div>
                        </div>
                        
                        <p className="text-muted mb-3">
                            Unlocking the account for <strong>{user.username}</strong> will:
                        </p>
                        <ul className="text-muted">
                            <li>Reset the failed login attempts counter to 0</li>
                            <li>Allow the user to attempt login again</li>
                            <li>Restore normal access to the system</li>
                        </ul>

                        {actionError && (
                            <div className="alert alert-danger mt-3" role="alert">
                                <Icon icon="Error" className="me-2" />
                                {actionError}
                            </div>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        isOutline
                        onClick={() => setUnlockModalOpen(false)}
                        isDisable={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="warning"
                        icon="LockOpen"
                        onClick={handleUnlockAccount}
                        isDisable={actionLoading}
                    >
                        {actionLoading ? (
                            <>
                                <Spinner size={16} className="me-2" />
                                Unlocking...
                            </>
                        ) : (
                            'Unlock Account'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Edit User Modal */}
            <Modal
                isOpen={editModalOpen}
                setIsOpen={setEditModalOpen}
                size="xl"
                titleId="edit-user-modal"
                isCentered
                isStaticBackdrop
                isScrollable
            >
                <ModalHeader setIsOpen={handleCloseEditModal}>
                    <ModalTitle id="edit-user-modal">
                        <Icon icon="Edit" className="me-2 text-primary" />
                        Edit User Details
                    </ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <div className="row g-4">
                        {actionError && (
                            <div className="col-12">
                                <div className="alert alert-danger mb-0" role="alert">
                                    <Icon icon="Error" className="me-2" />
                                    {actionError}
                                </div>
                            </div>
                        )}

                        {/* Personal Information Section */}
                        <div className="col-12">
                            <h6 className="text-muted border-bottom pb-2 mb-3">
                                <Icon icon="Person" className="me-2" />
                                Personal Information
                            </h6>
                        </div>

                        {/* Profile Photo Upload */}
                        <div className="col-12 text-center mb-3">
                            <div className="d-inline-block position-relative">
                                {(profilePhotoPreview || user?.profilePhotoUrl) ? (
                                    <img
                                        src={profilePhotoPreview || user?.profilePhotoUrl || ''}
                                        alt="Profile"
                                        className="rounded-circle"
                                        style={{ width: '120px', height: '120px', objectFit: 'cover', border: '3px solid #e9ecef' }}
                                    />
                                ) : (
                                    <div
                                        className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                        style={{ width: '120px', height: '120px', border: '3px dashed #dee2e6' }}
                                    >
                                        <Icon icon="Person" size="3x" color="secondary" />
                                    </div>
                                )}
                                <label
                                    htmlFor="edit-profile-photo"
                                    className="btn btn-sm btn-primary position-absolute"
                                    style={{ bottom: '0', right: '0', borderRadius: '50%', cursor: 'pointer' }}
                                    title="Upload new photo"
                                >
                                    <Icon icon="CameraAlt" />
                                </label>
                                <input
                                    type="file"
                                    id="edit-profile-photo"
                                    accept="image/*"
                                    className="d-none"
                                    onChange={handleProfilePhotoChange}
                                />
                                {(profilePhotoPreview || user?.profilePhotoUrl) && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger position-absolute"
                                        style={{ top: '0', right: '0', borderRadius: '50%', width: '24px', height: '24px', padding: '0' }}
                                        onClick={handleDeleteProfilePhoto}
                                        title="Remove photo"
                                        disabled={actionLoading}
                                    >
                                        <Icon icon="Close" size="sm" />
                                    </button>
                                )}
                            </div>
                            <p className="text-muted small mt-2">
                                {profilePhotoFile 
                                    ? `New photo selected: ${profilePhotoFile.name}` 
                                    : 'Click camera icon to upload profile photo (max 5MB)'}
                            </p>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-firstname" label="First Name" isFloating>
                                <Input
                                    type="text"
                                    placeholder="First Name"
                                    value={editFormData.firstname || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('firstname', e.target.value)}
                                    required
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-surname" label="Surname" isFloating>
                                <Input
                                    type="text"
                                    placeholder="Surname"
                                    value={editFormData.surname || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('surname', e.target.value)}
                                    required
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-email" label="Email Address" isFloating>
                                <Input
                                    type="email"
                                    placeholder="Email Address"
                                    value={editFormData.email || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('email', e.target.value)}
                                    required
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-dob" label="Date of Birth" isFloating>
                                <Input
                                    type="date"
                                    placeholder="Date of Birth"
                                    value={editFormData.dateOfBirth || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('dateOfBirth', e.target.value)}
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-phone" label="Phone Number" isFloating>
                                <Input
                                    type="tel"
                                    placeholder="Phone Number"
                                    value={editFormData.phoneNumber || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('phoneNumber', e.target.value)}
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-alternate-phone" label="Alternate Phone" isFloating>
                                <Input
                                    type="tel"
                                    placeholder="Alternate Phone"
                                    value={editFormData.alternatePhone || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('alternatePhone', e.target.value)}
                                />
                            </FormGroup>
                        </div>

                        {/* Employment Information Section */}
                        <div className="col-12 mt-4">
                            <h6 className="text-muted border-bottom pb-2 mb-3">
                                <Icon icon="Work" className="me-2" />
                                Employment Information
                            </h6>
                        </div>
                        
                        <div className="col-md-4">
                            <FormGroup id="edit-employee-id" label="Employee ID" isFloating>
                                <Input
                                    type="text"
                                    placeholder="Employee ID"
                                    value={editFormData.employeeId || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('employeeId', e.target.value)}
                                    disabled
                                />
                            </FormGroup>
                            <small className="text-muted">Employee ID cannot be changed</small>
                        </div>
                        
                        <div className="col-md-4">
                            <FormGroup id="edit-date-of-joining" label="Date of Joining" isFloating>
                                <Input
                                    type="date"
                                    placeholder="Date of Joining"
                                    value={editFormData.dateOfJoining || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('dateOfJoining', e.target.value)}
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-4">
                            <FormGroup id="edit-role" label="User Roles">
                                <div className="d-flex flex-wrap gap-2">
                                    {ROLE_OPTIONS.map(role => (
                                        <Button
                                            key={role.value}
                                            color={selectedRoles.includes(role.value) ? 'primary' : 'light'}
                                            isOutline={!selectedRoles.includes(role.value)}
                                            onClick={() => handleRoleToggle(role.value)}
                                            type="button"
                                            size="sm"
                                        >
                                            <Icon
                                                icon={selectedRoles.includes(role.value) ? 'CheckCircle' : 'RadioButtonUnchecked'}
                                                className="me-1"
                                            />
                                            {role.label}
                                        </Button>
                                    ))}
                                </div>
                            </FormGroup>
                            {selectedRoles.length === 0 && (
                                <small className="text-danger">At least one role is required</small>
                            )}
                        </div>
                        
                        <div className="col-md-4">
                            <FormGroup id="edit-department" label="Department">
                                <select
                                    className="form-select"
                                    value={editFormData.department || ''}
                                    onChange={(e) => handleEditFormChange('department', e.target.value)}
                                >
                                    <option value="">Select Department</option>
                                    <option value="Editorial">Editorial</option>
                                    <option value="Technology">Technology</option>
                                    <option value="Marketing">Marketing</option>
                                    <option value="Operations">Operations</option>
                                    <option value="HR">Human Resources</option>
                                    <option value="Finance">Finance</option>
                                    <option value="Content">Content</option>
                                    <option value="Management">Management</option>
                                </select>
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-4">
                            <FormGroup id="edit-designation" label="Designation" isFloating>
                                <Input
                                    type="text"
                                    placeholder="Designation"
                                    value={editFormData.designation || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('designation', e.target.value)}
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-reporting-manager" label="Reporting Manager ID" isFloating>
                                <Input
                                    type="text"
                                    placeholder="Reporting Manager ID"
                                    value={editFormData.reportingManagerId || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('reportingManagerId', e.target.value)}
                                />
                            </FormGroup>
                        </div>

                        {/* Address Section */}
                        <div className="col-12 mt-4">
                            <h6 className="text-muted border-bottom pb-2 mb-3">
                                <Icon icon="LocationOn" className="me-2" />
                                Address Information
                            </h6>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-address1" label="Address Line 1" isFloating>
                                <Input
                                    type="text"
                                    placeholder="Address Line 1"
                                    value={editFormData.addressLine1 || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('addressLine1', e.target.value)}
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-address2" label="Address Line 2" isFloating>
                                <Input
                                    type="text"
                                    placeholder="Address Line 2"
                                    value={editFormData.addressLine2 || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('addressLine2', e.target.value)}
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-3">
                            <FormGroup id="edit-city" label="City" isFloating>
                                <Input
                                    type="text"
                                    placeholder="City"
                                    value={editFormData.city || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('city', e.target.value)}
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-3">
                            <FormGroup id="edit-state" label="State" isFloating>
                                <Input
                                    type="text"
                                    placeholder="State"
                                    value={editFormData.state || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('state', e.target.value)}
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-3">
                            <FormGroup id="edit-country" label="Country" isFloating>
                                <Input
                                    type="text"
                                    placeholder="Country"
                                    value={editFormData.country || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('country', e.target.value)}
                                />
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-3">
                            <FormGroup id="edit-postal" label="Postal Code" isFloating>
                                <Input
                                    type="text"
                                    placeholder="Postal Code"
                                    value={editFormData.postalCode || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('postalCode', e.target.value)}
                                />
                            </FormGroup>
                        </div>

                        {/* Identity Verification Section */}
                        <div className="col-12 mt-4">
                            <h6 className="text-muted border-bottom pb-2 mb-3">
                                <Icon icon="VerifiedUser" className="me-2" />
                                Identity Verification
                            </h6>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-id-type" label="Identity Proof Type">
                                <select
                                    className="form-select"
                                    value={editFormData.identityProofType || ''}
                                    onChange={(e) => handleEditFormChange('identityProofType', e.target.value)}
                                >
                                    <option value="">Select ID Type</option>
                                    <option value="AADHAAR">Aadhaar Card</option>
                                    <option value="PAN">PAN Card</option>
                                    <option value="PASSPORT">Passport</option>
                                    <option value="DRIVING_LICENSE">Driving License</option>
                                    <option value="VOTER_ID">Voter ID</option>
                                    <option value="OTHER">Other</option>
                                </select>
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-id-number" label="Identity Proof Number" isFloating>
                                <Input
                                    type="text"
                                    placeholder="Identity Proof Number"
                                    value={editFormData.identityProofNumber || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditFormChange('identityProofNumber', e.target.value)}
                                />
                            </FormGroup>
                        </div>

                        {/* Identity Proof Document Upload */}
                        <div className="col-12">
                            <label className="form-label fw-semibold">Upload Identity Proof Document</label>
                            <div className="border rounded p-3 bg-light">
                                {/* Show existing identity proof document if available */}
                                {(user?.identityProofUrl && !identityProofFile) && (
                                    <div className="mb-3">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div className="d-flex align-items-center">
                                                <Icon icon="Description" size="lg" className="me-2 text-primary" />
                                                <span className="fw-bold">Current Identity Proof Document</span>
                                            </div>
                                            <div>
                                                <a
                                                    href={user.identityProofUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary me-2"
                                                >
                                                    <Icon icon="Visibility" className="me-1" />
                                                    View
                                                </a>
                                                <a
                                                    href={user.identityProofUrl}
                                                    download
                                                    className="btn btn-sm btn-outline-secondary me-2"
                                                >
                                                    <Icon icon="Download" className="me-1" />
                                                    Download
                                                </a>
                                                <button
                                                    type="button"
                                                    onClick={handleDeleteIdentityProof}
                                                    className="btn btn-sm btn-outline-danger"
                                                    disabled={actionLoading}
                                                >
                                                    <Icon icon="Delete" className="me-1" />
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                        {/* Show preview if it's an image */}
                                        {user.identityProofUrl.match(/\.(jpg|jpeg|png|gif)$/i) && (
                                            <div className="text-center mb-2">
                                                <img
                                                    src={user.identityProofUrl}
                                                    alt="Identity Proof"
                                                    className="img-thumbnail"
                                                    style={{ maxHeight: '150px', maxWidth: '100%' }}
                                                />
                                            </div>
                                        )}
                                        {/* Show PDF icon if it's a PDF */}
                                        {user.identityProofUrl.match(/\.pdf$/i) && (
                                            <div className="text-center mb-2 p-3 bg-white rounded">
                                                <Icon icon="PictureAsPdf" size="3x" color="danger" />
                                                <p className="mb-0 mt-2 text-muted">PDF Document</p>
                                            </div>
                                        )}
                                        <div className="alert alert-info mb-0 mt-2">
                                            <Icon icon="Info" className="me-2" />
                                            To replace the document, select a new file below.
                                        </div>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    id="edit-identity-proof"
                                    accept="image/*,.pdf"
                                    className="form-control"
                                    onChange={handleIdentityProofChange}
                                />
                                <small className="text-muted d-block mt-2">
                                    Accepted formats: JPEG, PNG, GIF, PDF (max 10MB)
                                </small>
                                {identityProofFile && (
                                    <div className="mt-2 text-success">
                                        <Icon icon="CheckCircle" className="me-2" />
                                        New file selected: {identityProofFileName}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Preferences Section */}
                        <div className="col-12 mt-4">
                            <h6 className="text-muted border-bottom pb-2 mb-3">
                                <Icon icon="Settings" className="me-2" />
                                Preferences
                            </h6>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-language" label="Language Preference">
                                <select
                                    className="form-select"
                                    value={editFormData.languagePreference || 'en'}
                                    onChange={(e) => handleEditFormChange('languagePreference', e.target.value)}
                                >
                                    <option value="en">English</option>
                                    <option value="hi">Hindi</option>
                                    <option value="te">Telugu</option>
                                    <option value="fr">French</option>
                                    <option value="de">German</option>
                                    <option value="tr">Turkish</option>
                                </select>
                            </FormGroup>
                        </div>
                        
                        <div className="col-md-6">
                            <FormGroup id="edit-timezone" label="Timezone">
                                <select
                                    className="form-select"
                                    value={editFormData.timezone || 'UTC'}
                                    onChange={(e) => handleEditFormChange('timezone', e.target.value)}
                                >
                                    <option value="UTC">UTC</option>
                                    <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                                    <option value="America/New_York">America/New_York (EST)</option>
                                    <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                                    <option value="Europe/London">Europe/London (GMT)</option>
                                    <option value="Europe/Paris">Europe/Paris (CET)</option>
                                    <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                                </select>
                            </FormGroup>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        isOutline
                        onClick={handleCloseEditModal}
                        isDisable={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        icon="Save"
                        onClick={handleUpdateUser}
                        isDisable={actionLoading || !editFormData.firstname?.trim() || !editFormData.surname?.trim() || !editFormData.email?.trim()}
                    >
                        {actionLoading ? (
                            <>
                                <Spinner size={16} className="me-2" />
                                Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
            {/* State Assignment Modal */}
            {isContentRole() && (
                <StateAssignmentModal
                    isOpen={stateAssignmentModalOpen}
                    setIsOpen={setStateAssignmentModalOpen}
                    userId={userId || ''}
                    userName={getFullName()}
                />
            )}
        </>
    );
};

export default UserPage;